import type { FieldProps } from 'formik';
import { useMemo } from 'react';

import { Select, Skeleton } from '@frontend/legacy-components';

import { useFeatureFlagFormContext } from 'modules/common/drawers/featureFlagDrawer/components/FormWrapper/FeatureFlagFormContext';
import {
  FeatureFlagData,
  FeatureFlagType,
} from 'modules/common/drawers/featureFlagDrawer/types';

type SelectOption = {
  label: string;
  value: string;
};

export const StringSelectInput = ({
  field,
  form,
  flagData,
}: FieldProps<string> & {
  flagData: Extract<FeatureFlagData, { type: FeatureFlagType.String }>;
}) => {
  const {
    featureFlagsQueryResult: { isLoading },
  } = useFeatureFlagFormContext();

  const { name: fieldName, value: fieldValue, onBlur } = field;
  const { setFieldValue } = form;

  const options: SelectOption[] = useMemo(
    () =>
      flagData.allowedValues.map((value) => ({
        label: value,
        value: value,
      })),
    [flagData.allowedValues],
  );

  const selectedOption = useMemo(
    () => options.find((option) => option.value === fieldValue),
    [options, fieldValue],
  );

  if (!fieldName) {
    return null;
  }

  return (
    <Skeleton
      isLoaded={!isLoading}
      height={3}
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
    >
      <Select
        size="sm"
        name={fieldName}
        value={selectedOption || null}
        onBlur={onBlur}
        onChange={(option: SelectOption | null) => {
          setFieldValue(fieldName, option?.value || null);
        }}
        options={options}
        isClearable={false}
        minWidth={16}
      />
    </Skeleton>
  );
};
